import { useTranslation } from 'react-i18next'
import { getMediaUrl } from '@/utils/media'
import { motion } from 'framer-motion'
import { ArrowRight, CheckCircle2 } from 'lucide-react'
import { SEO } from '@/components/SEO'
import { Hero } from '@/components/Hero'
import { Section } from '@/components/Section'
import { ProductCard } from '@/components/ProductCard'
import { Button } from '@/components/ui/Button'
import { LocalizedLink } from '@/components/LocalizedLink'

export function HomePage() {
  const { t } = useTranslation()

  const products = [
    {
      title: t('products.cathodicProtection.title'),
      description: t('products.cathodicProtection.shortDesc'),
      image: getMediaUrl('products/cathodic-protection/fotoCP.jpg'),
      imageAlt: 'MMO Anodes for Cathodic Protection installation',
      href: '/cathodic-protection',
    },
    {
      title: t('products.oxygenEvolution.title'),
      description: t('products.oxygenEvolution.shortDesc'),
      image: getMediaUrl('homepage/oxygen-evolution-card-thumbnail.png'),
      imageAlt: 'Oxygen Evolution MMO anodes',
      href: '/oxygen-evolution',
    },
    {
      title: t('products.electrochlorination.title'),
      description: t('products.electrochlorination.shortDesc'),
      image: getMediaUrl('products/electrochlorination/pool-anodes-application.jpg'),
      imageAlt: 'Pool water treatment anodes',
      href: '/electrochlorination',
    },
    {
      title: t('products.hiseochlor.title'),
      description: t('products.hiseochlor.shortDesc'),
      image: getMediaUrl('products/electrochlorination/hiseochlor1.png'),
      imageAlt: 'HISEOCHLOR electrolyzer packages',
      href: '/hiseochlor',
    },
    {
      title: t('products.chlorineAlkali.title'),
      description: t('products.chlorineAlkali.shortDesc'),
      image: getMediaUrl('products/chlorine-alkali/chlorine-alkali-equipment.jpg'),
      imageAlt: 'Chlorine alkali production equipment',
      href: '/chlorine-alkali',
    },
  ]

  const aboutPoints = [
    t('home.aboutPoint1'),
    t('home.aboutPoint2'),
    t('home.aboutPoint3'),
  ]

  return (
    <>
      <SEO
        title={t('home.metaTitle')}
        description={t('home.metaDescription')}
      />

      {/* Hero Section */}
      <Hero
        pretitle={t('home.heroPretitle')}
        title={t('home.heroTitle')}
        subtitle={t('home.heroSubtitle')}
        backgroundVideo={getMediaUrl('video/202602032145.mp4')}
        size="lg"
        showScrollButton
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <a href="#products" onClick={(e) => { e.preventDefault(); document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' }) }}>
            <Button variant="primary" size="lg">
              {t('buttons.exploreProducts')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
          <LocalizedLink to="/contact">
            <Button variant="secondary" size="lg">
              {t('buttons.contactUs')}
            </Button>
          </LocalizedLink>
        </div>
      </Hero>

      {/* Products Section */}
      <Section
        id="products"
        title={t('home.productsTitle')}
        subtitle={t('home.productsSubtitle')}
        centered
        background="light"
      >
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {products.map((product, index) => (
            <ProductCard
              key={product.href}
              {...product}
              index={index}
            />
          ))}
        </div>
      </Section>

      {/* About Section */}
      <Section background="white">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl" style={{ color: '#0f172a' }}>
              {t('home.aboutTitle')}
            </h2>
            <p className="mt-4 text-lg" style={{ color: '#64748b' }}>
              {t('home.aboutDescription')}
            </p>
            <ul className="mt-8 space-y-4">
              {aboutPoints.map((point, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: '#00a3cc' }} />
                  <span style={{ color: '#334155' }}>{point}</span>
                </motion.li>
              ))}
            </ul>
            <LocalizedLink to="/about" className="mt-8 inline-block">
              <Button variant="secondary">
                {t('buttons.learnMore')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </LocalizedLink>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="grid gap-4 sm:grid-cols-2"
          >
            <img
              src={getMediaUrl('about/laboratory-equipment.jpg')}
              alt={t('home.labImageAlt')}
              className="rounded-xl shadow-lg"
              loading="lazy"
            />
            <img
              src={getMediaUrl('about/manufacturing-process.jpeg')}
              alt={t('home.manufacturingImageAlt')}
              className="rounded-xl shadow-lg sm:mt-8"
              loading="lazy"
            />
          </motion.div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="accent" centered>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mx-auto max-w-2xl"
        >
          <h2 className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {t('home.ctaTitle')}
          </h2>
          <p className="mt-4 text-lg text-white/80">
            {t('home.ctaDescription')}
          </p>
          <LocalizedLink to="/contact" className="mt-8 inline-block">
            <Button variant="secondary" size="lg">
              {t('buttons.getQuote')}
            </Button>
          </LocalizedLink>
        </motion.div>
      </Section>
    </>
  )
}
